import { defineStore } from "pinia";
import { ref, computed } from "vue";

/**
 * Create an unsubscribe function for event handlers
 * @param {Array} handlersArray - The reactive array of handlers
 * @param {Function} handler - The handler to remove
 * @returns {Function} Unsubscribe function
 */
const createUnsubscribe = (handlersArray, handler) => {
  return () => {
    const index = handlersArray.value.indexOf(handler);
    if (index > -1) {
      handlersArray.value.splice(index, 1);
    }
  };
};

/**
 * Subscribe a handler to an event, with optional duplicate prevention
 * @param {Array} handlersArray - The reactive array of handlers
 * @param {Function} handler - The handler to add
 * @param {boolean} preventDuplicates - Whether to prevent duplicate handlers
 * @returns {Function} Unsubscribe function
 */
const subscribeHandler = (handlersArray, handler, preventDuplicates = false) => {
  if (preventDuplicates && handlersArray.value.includes(handler)) {
    return createUnsubscribe(handlersArray, handler);
  }
  handlersArray.value.push(handler);
  return createUnsubscribe(handlersArray, handler);
};

export const useIQOverlayStore = defineStore("iqOverlay", () => {
  // State
  const isOpen = ref(false);
  const initialPrompt = ref(null);
  const type = ref("");
  const showSuggestions = ref(false);
  const roleName = ref("");
  const roleId = ref(null);
  // Import Job Description (position import) session state
  const positionId = ref(null);
  const positionName = ref("");
  const showJobDescriptionUploadDialog = ref(false);
  const uploadedFile = ref(null);
  const suggestedResponsibilities = ref([]);
  const isCreatingResponsibility = ref(false);
  const creationError = ref("");
  const useCustomLoader = ref(true); // Default to custom IQLoader
  // Checkpoint suggestion state
  const showCheckpointSuggestions = ref(false);
  const responsibilityId = ref(null);
  const suggestedCheckpoints = ref([]);
  const isCreatingCheckpoint = ref(false);
  const checkpointCreationError = ref("");

  // Video instructions state
  const videoUrl = ref(null);
  const checkpointId = ref(null);
  const checkpointPrefix = ref("");
  const checkpointCriteria = ref("");
  const checkpointInstructions = ref(""); // business_purpose for API
  const responsibilityCode = ref("");
  const responsibilityName = ref("");
  const responsibilityPurpose = ref(""); // responsibility_purpose for API
  const suggestedInstructions = ref(null);
  const showVideoInstructionsPane = ref(false);
  /** { videoType, testingType } from GenerateVideoMicrolearningDialog; null = default inspection payload */
  const videoToComponentsSelection = ref(null);

  // Getters
  const hasOpenOverlay = computed(() => isOpen.value);
  const hasSuggestions = computed(() => suggestedResponsibilities.value.length > 0);
  const hasCheckpointSuggestions = computed(() => suggestedCheckpoints.value.length > 0);

  /**
   * Set core overlay state from data
   * @param {Object} data - The overlay data
   */
  const setCoreState = (data) => {
    initialPrompt.value = data.prompt || null;
    type.value = data.type || "";
    roleName.value = data.roleName || "";
    roleId.value = data.roleId ?? null;
    positionId.value = data.positionId ?? null;
    positionName.value = data.positionName || "";
    if (data.uploadedFile) {
      uploadedFile.value = data.uploadedFile;
    }
    suggestedResponsibilities.value = data.suggestedResponsibilities || [];
    useCustomLoader.value = data.useCustomLoader === undefined ? undefined : data.useCustomLoader;
  };

  /**
   * Set checkpoint-related state from data
   * @param {Object} data - The overlay data
   */
  const setCheckpointState = (data) => {
    responsibilityName.value = data.responsibilityName || "";
    responsibilityId.value = data.responsibilityId || null;
    suggestedCheckpoints.value = data.suggestedCheckpoints || [];
  };

  /**
   * Set video instructions state from data
   * @param {Object} data - The overlay data
   */
  const setVideoInstructionsState = (data) => {
    videoUrl.value = data.videoUrl || null;
    checkpointId.value = data.checkpointId || null;
    checkpointPrefix.value = data.checkpointPrefix || "";
    checkpointCriteria.value = data.checkpointCriteria || "";
    checkpointInstructions.value = data.checkpointInstructions || "";
    responsibilityCode.value = data.responsibilityCode || "";
    responsibilityName.value = data.responsibilityName || "";
    responsibilityPurpose.value = data.responsibilityPurpose || "";
    videoToComponentsSelection.value = data.videoToComponentsSelection ?? null;
  };

  // Actions
  const openOverlay = (data = {}) => {
    setCoreState(data);
    setCheckpointState(data);
    setVideoInstructionsState(data);

    // Reset video instructions pane
    showVideoInstructionsPane.value = false;
    suggestedInstructions.value = null;

    isOpen.value = true;
  };

  /**
   * Reset core overlay state to defaults
   */
  const resetCoreState = () => {
    initialPrompt.value = null;
    type.value = "";
    showSuggestions.value = false;
    roleName.value = "";
    roleId.value = null;
    positionId.value = null;
    positionName.value = "";
    showJobDescriptionUploadDialog.value = false;
    uploadedFile.value = null;
    suggestedResponsibilities.value = [];
    isCreatingResponsibility.value = false;
    creationError.value = "";
    useCustomLoader.value = true;
  };

  /**
   * Reset checkpoint-related state to defaults
   */
  const resetCheckpointState = () => {
    showCheckpointSuggestions.value = false;
    responsibilityName.value = "";
    responsibilityId.value = null;
    suggestedCheckpoints.value = [];
    isCreatingCheckpoint.value = false;
    checkpointCreationError.value = "";
  };

  /**
   * Reset video instructions state to defaults
   */
  const resetVideoInstructionsState = () => {
    videoUrl.value = null;
    checkpointId.value = null;
    checkpointPrefix.value = "";
    checkpointCriteria.value = "";
    checkpointInstructions.value = "";
    responsibilityCode.value = "";
    responsibilityPurpose.value = "";
    suggestedInstructions.value = null;
    showVideoInstructionsPane.value = false;
    videoToComponentsSelection.value = null;
  };

  const closeOverlay = () => {
    isOpen.value = false;
    resetCoreState();
    resetVideoInstructionsState();
    resetCheckpointState();
  };

  const openJobDescriptionUpload = (data = {}) => {
    positionId.value = data.positionId ?? positionId.value;
    positionName.value = data.positionName || positionName.value || "";
    type.value = "positionImport";
    showJobDescriptionUploadDialog.value = true;
  };

  const closeJobDescriptionUpload = () => {
    showJobDescriptionUploadDialog.value = false;
    if (!isOpen.value) {
      resetCoreState();
    }
  };

  const completeJobDescriptionUpload = (file) => {
    uploadedFile.value = file || null;
    showJobDescriptionUploadDialog.value = false;
    type.value = "positionImport";
    isOpen.value = true;
  };

  const reopenJobDescriptionUpload = () => {
    showJobDescriptionUploadDialog.value = true;
  };

  const closeNavigator = () => {
    showSuggestions.value = false;
  };

  const closeCheckpointNavigator = () => {
    showCheckpointSuggestions.value = false;
  };

  const updateCheckpointSuggestions = (data) => {
    if (data.suggestions) {
      suggestedCheckpoints.value = data.suggestions;
    }
    if (data.responsibilityName) {
      responsibilityName.value = data.responsibilityName;
    }
    showCheckpointSuggestions.value = true;
  };

  const removeCheckpointSuggestion = (index) => {
    if (suggestedCheckpoints.value && index >= 0 && index < suggestedCheckpoints.value.length) {
      suggestedCheckpoints.value.splice(index, 1);
    }
  };

  const setCreatingCheckpoint = (value) => {
    isCreatingCheckpoint.value = value;
  };

  const setCheckpointCreationError = (error) => {
    checkpointCreationError.value = error || "";
  };

  const updateSuggestions = (data) => {
    if (data.suggestions) {
      suggestedResponsibilities.value = data.suggestions;
    }
    if (data.roleName) {
      roleName.value = data.roleName;
    }
    showSuggestions.value = true;
  };

  const removeSuggestion = (index) => {
    if (
      suggestedResponsibilities.value &&
      index >= 0 &&
      index < suggestedResponsibilities.value.length
    ) {
      suggestedResponsibilities.value.splice(index, 1);
    }
  };

  const updateResponsibilities = (responsibilities) => {
    suggestedResponsibilities.value = responsibilities;
    showSuggestions.value = !!(responsibilities && responsibilities.length > 0);
  };

  const setCreatingResponsibility = (value) => {
    isCreatingResponsibility.value = value;
  };

  const setCreationError = (error) => {
    creationError.value = error || "";
  };

  // Video instructions actions
  const setSuggestedInstructions = (data) => {
    suggestedInstructions.value = data;
    showVideoInstructionsPane.value = true;
  };

  const closeVideoInstructionsPane = () => {
    showVideoInstructionsPane.value = false;
  };

  const clearVideoInstructions = () => {
    suggestedInstructions.value = null;
    showVideoInstructionsPane.value = false;
  };

  // Event handlers that can be subscribed to
  const actionClickHandlers = ref([]);
  const createAndAttachHandlers = ref([]);
  const rejectHandlers = ref([]);
  const suggestMoreHandlers = ref([]);
  const editHandlers = ref([]);
  const submitHandlers = ref([]);
  // Checkpoint event handlers
  const addCheckpointHandlers = ref([]);
  const rejectCheckpointHandlers = ref([]);
  const suggestMoreCheckpointsHandlers = ref([]);
  const refreshCheckpointHandlers = ref([]);
  const checkpointCreatedHandlers = ref([]);

  // Subscribe to events
  const onActionClick = (handler) => subscribeHandler(actionClickHandlers, handler);
  const onCreateAndAttach = (handler) => subscribeHandler(createAndAttachHandlers, handler);
  const onReject = (handler) => subscribeHandler(rejectHandlers, handler);
  const onSuggestMore = (handler) => subscribeHandler(suggestMoreHandlers, handler);
  const onEdit = (handler) => subscribeHandler(editHandlers, handler);
  const onSubmit = (handler) => subscribeHandler(submitHandlers, handler);

  // Checkpoint event subscriptions (with duplicate prevention)
  const onAddCheckpoint = (handler) => subscribeHandler(addCheckpointHandlers, handler, true);
  const onRejectCheckpoint = (handler) => subscribeHandler(rejectCheckpointHandlers, handler, true);
  const onSuggestMoreCheckpoints = (handler) =>
    subscribeHandler(suggestMoreCheckpointsHandlers, handler);
  const onRefreshCheckpoint = (handler) => subscribeHandler(refreshCheckpointHandlers, handler);
  const onCheckpointCreated = (handler) => subscribeHandler(checkpointCreatedHandlers, handler);

  // Emit events
  const emitActionClick = (action) => {
    actionClickHandlers.value.forEach((handler) => handler(action));
  };

  const emitCreateAndAttach = (data) => {
    // ER-12405: RoleResponsibilitiesTab and RolePageView both subscribe with
    // different handlers; forEach would create+attach twice on one click.
    // Last subscriber is the live page. Set creating before invoke so a
    // second click in the same tick is ignored.
    if (isCreatingResponsibility.value) {
      return;
    }
    const handler = createAndAttachHandlers.value[createAndAttachHandlers.value.length - 1];
    if (!handler) {
      return;
    }
    isCreatingResponsibility.value = true;
    handler(data);
  };

  const emitReject = (data) => {
    rejectHandlers.value.forEach((handler) => handler(data));
  };

  const emitSuggestMore = () => {
    suggestMoreHandlers.value.forEach((handler) => handler());
  };

  const emitEdit = (data) => {
    editHandlers.value.forEach((handler) => handler(data));
  };

  const emitSubmit = (data) => {
    submitHandlers.value.forEach((handler) => handler(data));
  };

  // Checkpoint event emissions
  const emitAddCheckpoint = (data) => {
    // Prevent duplicate emissions - if already creating, ignore subsequent calls
    if (isCreatingCheckpoint.value) {
      return;
    }
    addCheckpointHandlers.value.forEach((handler) => handler(data));
  };

  const emitRejectCheckpoint = (data) => {
    rejectCheckpointHandlers.value.forEach((handler) => handler(data));
  };

  const emitSuggestMoreCheckpoints = () => {
    suggestMoreCheckpointsHandlers.value.forEach((handler) => handler());
  };

  const emitRefreshCheckpoint = () => {
    refreshCheckpointHandlers.value.forEach((handler) => handler());
  };

  /**
   * Emit when a checkpoint was created (e.g. from IQ overlay).
   * Payload: { responsibilityId }. Listeners (e.g. ResponsibilityCard) can refresh counts/table.
   */
  const emitCheckpointCreated = (responsibilityId) => {
    const payload = { responsibilityId };
    checkpointCreatedHandlers.value.forEach((handler) => handler(payload));
  };

  return {
    // State
    isOpen,
    initialPrompt,
    type,
    showSuggestions,
    roleName,
    roleId,
    positionId,
    positionName,
    showJobDescriptionUploadDialog,
    uploadedFile,
    suggestedResponsibilities,
    isCreatingResponsibility,
    creationError,
    useCustomLoader,
    // Checkpoint state
    showCheckpointSuggestions,
    responsibilityName,
    responsibilityId,
    suggestedCheckpoints,
    isCreatingCheckpoint,
    checkpointCreationError,

    // Video instructions state
    videoUrl,
    checkpointId,
    checkpointPrefix,
    checkpointCriteria,
    checkpointInstructions,
    responsibilityCode,
    responsibilityPurpose,
    suggestedInstructions,
    showVideoInstructionsPane,
    videoToComponentsSelection,

    // Getters
    hasOpenOverlay,
    hasSuggestions,
    hasCheckpointSuggestions,

    // Actions
    openOverlay,
    closeOverlay,
    openJobDescriptionUpload,
    closeJobDescriptionUpload,
    completeJobDescriptionUpload,
    reopenJobDescriptionUpload,
    closeNavigator,
    updateSuggestions,
    removeSuggestion,
    updateResponsibilities,
    setCreatingResponsibility,
    setCreationError,
    // Checkpoint actions
    closeCheckpointNavigator,
    updateCheckpointSuggestions,
    removeCheckpointSuggestion,
    setCreatingCheckpoint,
    setCheckpointCreationError,

    // Video instructions actions
    setSuggestedInstructions,
    closeVideoInstructionsPane,
    clearVideoInstructions,

    // Event subscription
    onActionClick,
    onCreateAndAttach,
    onReject,
    onSuggestMore,
    onEdit,
    onSubmit,
    // Checkpoint event subscription
    onAddCheckpoint,
    onRejectCheckpoint,
    onSuggestMoreCheckpoints,
    onRefreshCheckpoint,
    onCheckpointCreated,

    // Event emission
    emitActionClick,
    emitCreateAndAttach,
    emitReject,
    emitSuggestMore,
    emitEdit,
    emitSubmit,
    // Checkpoint event emission
    emitAddCheckpoint,
    emitRejectCheckpoint,
    emitSuggestMoreCheckpoints,
    emitRefreshCheckpoint,
    emitCheckpointCreated,
  };
});
